import type { InfoLinkConferma, ModalitaPartecipazione, PercorsoPartecipante, PercorsoPubblico, UtenteCorrente, WebinarPartecipante } from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiaveMe } from './auth';
import { richiesta } from './client';

export const chiavi = {
  percorsi: ['percorsi'] as const,
  webinar: (id: string) => ['webinar', id] as const,
  percorsoPubblico: (id: string) => ['percorso-pubblico', id] as const,
  linkConferma: (token: string) => ['link-conferma', token] as const,
  completaIscrizione: (token: string) => ['completa-iscrizione', token] as const,
};

export function usePercorsi() {
  return useQuery({ queryKey: chiavi.percorsi, queryFn: async () => (await richiesta<PercorsoPartecipante[]>('/percorsi')).dati });
}

export function useWebinarPartecipante(id: string) {
  return useQuery({ queryKey: chiavi.webinar(id), queryFn: async () => (await richiesta<WebinarPartecipante>(`/webinar/${id}`)).dati });
}

export function useConfermaWebinar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (modalita: ModalitaPartecipazione) => (await richiesta<unknown>(`/webinar/${id}/conferma`, { method: 'POST', json: { modalita } })).status,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiavi.percorsi });
      void qc.invalidateQueries({ queryKey: chiavi.webinar(id) });
    },
  });
}

export function useRinunciaWebinar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<unknown>(`/webinar/${id}/rinuncia`, { method: 'POST' })).status,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiavi.percorsi });
      void qc.invalidateQueries({ queryKey: chiavi.webinar(id) });
    },
  });
}

// ---- pubbliche (senza sessione)
export function usePercorsoPubblico(id: string | null) {
  return useQuery({
    queryKey: chiavi.percorsoPubblico(id ?? ''),
    queryFn: async () => (await richiesta<PercorsoPubblico>(`/percorsi/pubblici/${id}`)).dati,
    enabled: Boolean(id),
    retry: false,
  });
}

export function useLinkConferma(token: string) {
  return useQuery({
    queryKey: chiavi.linkConferma(token),
    queryFn: async () => (await richiesta<InfoLinkConferma>(`/conferme/link/${encodeURIComponent(token)}`)).dati,
    retry: false,
  });
}

export type AzioneLink = 'conferma_online' | 'conferma_sala' | 'rinuncia';
export function useAzioneLinkConferma(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (azione: AzioneLink) =>
      (await richiesta<InfoLinkConferma>(`/conferme/link/${encodeURIComponent(token)}`, { method: 'POST', json: { azione } })).dati,
    onSuccess: (info) => qc.setQueryData(chiavi.linkConferma(token), info),
  });
}

export interface InfoCompletaIscrizione {
  nome: string;
  email: string;
  percorsoTitolo: string;
}
export function useInfoCompletaIscrizione(token: string) {
  return useQuery({
    queryKey: chiavi.completaIscrizione(token),
    queryFn: async () => (await richiesta<InfoCompletaIscrizione>(`/auth/completa-iscrizione/${encodeURIComponent(token)}`)).dati,
    retry: false,
  });
}
export function useCompletaIscrizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { token: string; password: string }) =>
      (await richiesta<{ esito: 'ok'; utente: UtenteCorrente }>('/auth/completa-iscrizione', { method: 'POST', json: input })).dati,
    onSuccess: ({ utente }) => qc.setQueryData(chiaveMe, utente),
  });
}
